/**
 * StepperDemo — reusable step-by-step walkthrough panel for explainers.
 *
 * Provides a numbered sequence of steps the reader can advance through,
 * with progress dots, Next/Reset buttons, and an accent-tinted container.
 * Used by FibrationExplainer (lift construction) and available to future
 * explainers (e.g., SNF phases, bridge construction).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { FONT, GRAY, withAlpha } from "../../design-system/tokens.js";

export interface StepperStep {
  readonly label: string;
  readonly description: string;
}

export interface StepperDemoProps {
  /** Ordered steps to walk through. */
  readonly steps: readonly StepperStep[];
  /** Accent color for the panel chrome. */
  readonly accent: string;
  /** Title shown above the stepper (e.g., "Lift construction"). */
  readonly title: string;
  /** Accessible label for the container. */
  readonly ariaLabel: string;
}

export function StepperDemo({
  steps,
  accent,
  title,
  ariaLabel,
}: StepperDemoProps): React.JSX.Element | null {
  const [step, setStep] = useState(0);
  const advance = useCallback(() => {
    setStep((s) => Math.min(s + 1, steps.length - 1));
  }, [steps.length]);
  const reset = useCallback(() => {
    setStep(0);
  }, []);

  // step is always in [0, steps.length-1] via advance/reset; this guard
  // satisfies the linter and protects against an empty steps array.
  const currentStep = steps[step];
  if (!currentStep) return null;

  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem",
        backgroundColor: withAlpha(accent, "0D"),
        borderRadius: "0.5rem",
        border: `1px solid ${withAlpha(accent, "33")}`,
      }}
      aria-label={ariaLabel}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.5rem",
        }}
      >
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: accent,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {title} — step {step + 1} of {steps.length}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.25rem" }}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: i <= step ? accent : GRAY[300],
                transition: "background-color 0.2s",
              }}
            />
          ))}
        </div>
      </div>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: GRAY[900],
          marginBottom: "0.25rem",
        }}
      >
        {currentStep.label}
      </div>
      <p style={{ margin: "0 0 0.5rem", fontSize: "0.8125rem", color: GRAY[700] }}>
        {currentStep.description}
      </p>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          onClick={advance}
          disabled={step >= steps.length - 1}
          style={{
            appearance: "none",
            border: `1px solid ${accent}`,
            background: withAlpha(accent, "1A"),
            borderRadius: "0.3rem",
            padding: "0.25rem 0.6rem",
            cursor: step >= steps.length - 1 ? "not-allowed" : "pointer",
            fontSize: "0.75rem",
            color: accent,
            fontWeight: 600,
            fontFamily: FONT.body,
            opacity: step >= steps.length - 1 ? 0.5 : 1,
          }}
        >
          Next step →
        </button>
        {step > 0 && (
          <button
            type="button"
            onClick={reset}
            style={{
              appearance: "none",
              border: `1px solid ${GRAY[300]}`,
              background: "white",
              borderRadius: "0.3rem",
              padding: "0.25rem 0.6rem",
              cursor: "pointer",
              fontSize: "0.75rem",
              color: GRAY[600],
              fontWeight: 500,
              fontFamily: FONT.body,
            }}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
